import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ListContainer from '../components/ListContainer';
import API_BASE_URL from '../config/api';

const InspectorTransactionListPage = ({ user }) => {
  const navigate = useNavigate();

  // Toast Notification State
  const [toastMessage, setToastMessage] = useState('');

  // Societies & Selected filter
  const [societies, setSocieties] = useState([]);
  const [societiesLoaded, setSocietiesLoaded] = useState(false);
  const [societyInput, setSocietyInput] = useState('ALL');
  const [activeSocietyId, setActiveSocietyId] = useState('ALL');

  // Periods State
  const [periods, setPeriods] = useState([]);
  const [periodInput, setPeriodInput] = useState('');
  const [activePeriodId, setActivePeriodId] = useState('');
  const [exporting, setExporting] = useState(false);

  // Date Range Filter States
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Table Data & Pagination States
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch Periods on mount
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/periods`);
        const data = await res.json();
        if (data.success && Array.isArray(data.periods) && data.periods.length > 0) {
          setPeriods(data.periods);
          setPeriodInput(data.periods[0]._id);
          setActivePeriodId(data.periods[0]._id);
        }
      } catch (err) {
        console.error('Error fetching periods:', err);
      }
    };
    fetchPeriods();
  }, []);

  // Fetch assigned societies for Inspector or all for Power / RCS
  useEffect(() => {
    const fetchSocieties = async () => {
      try {
        const role = (user?.role || '').toLowerCase();
        const isPowerOrRCS = role === 'power' || role === 'rcs';
        const endpoint = isPowerOrRCS
          ? `${API_BASE_URL}/api/societies/all`
          : `${API_BASE_URL}/api/societies/inspector?email=${encodeURIComponent(user.email || '')}&phone=${encodeURIComponent(user.phone || '')}&name=${encodeURIComponent(user.name || '')}`;
        const res = await fetch(endpoint);
        const data = await res.json();
        if (data.success && Array.isArray(data.societies)) {
          setSocieties(data.societies);
        }
      } catch (err) {
        console.error('Error fetching societies:', err);
      } finally {
        setSocietiesLoaded(true);
      }
    };
    fetchSocieties();
  }, [user]);

  // Fetch Transactions from backend
  const fetchTransactions = useCallback(async () => {
    const role = (user?.role || '').toLowerCase();
    const isPowerOrRCS = role === 'power' || role === 'rcs';

    // Guard: Wait for assigned societies to load first if non-power/RCS user
    if (!isPowerOrRCS && !societiesLoaded) {
      return;
    }

    setLoading(true);
    try {
      let socParam = '';
      if (activeSocietyId === 'ALL') {
        if (isPowerOrRCS) {
          socParam = ''; // Empty string means all societies in backend query
        } else {
          // If inspector has no assigned societies mapped, return 0 results
          if (societies.length === 0) {
            setTransactions([]);
            setTotalCount(0);
            setTotalPages(1);
            setLoading(false);
            return;
          }
          // Pass all assigned society IDs as comma-separated list
          socParam = societies.map(s => s._id).join(',');
        }
      } else {
        socParam = activeSocietyId;
      }

      const queryParams = new URLSearchParams({
        societyId: socParam,
        periodId: activePeriodId,
        startDate,
        endDate,
        page,
        limit
      });

      const res = await fetch(`${API_BASE_URL}/api/transactions?${queryParams.toString()}`);
      const data = await res.json();

      if (data.success) {
        setTransactions(data.transactions || []);
        setTotalCount(data.totalCount || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching inspector transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [activeSocietyId, societies, societiesLoaded, activePeriodId, startDate, endDate, page, limit, user]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Search Action
  const handleSearchSubmit = () => {
    setActiveSocietyId(societyInput);
    setActivePeriodId(periodInput);
    setStartDate(startDateInput);
    setEndDate(endDateInput);
    setPage(1);
  };

  // Clear Action
  const handleClearFilters = () => {
    setSocietyInput('ALL');
    setActiveSocietyId('ALL');
    setStartDateInput('');
    setEndDateInput('');
    setStartDate('');
    setEndDate('');
    if (periods.length > 0) {
      setPeriodInput(periods[0]._id);
      setActivePeriodId(periods[0]._id);
    }
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Export CSV Handler (Downloads all eligible records matching current filter)
  const handleExportCSV = async () => {
    if (totalCount === 0 && transactions.length === 0) return;
    try {
      setExporting(true);
      let socParam = '';
      if (activeSocietyId === 'ALL') {
        const role = (user?.role || '').toLowerCase();
        const isPowerOrRCS = role === 'power' || role === 'rcs';
        if (isPowerOrRCS) {
          socParam = '';
        } else {
          socParam = societies.map(s => s._id).join(',');
        }
      } else {
        socParam = activeSocietyId;
      }

      const queryParams = new URLSearchParams({
        societyId: socParam,
        periodId: activePeriodId,
        startDate,
        endDate,
        page: 1,
        limit: Math.max(totalCount, 100000)
      });

      const res = await fetch(`${API_BASE_URL}/api/transactions?${queryParams.toString()}`);
      const data = await res.json();
      const exportTx = (data.success && data.transactions) ? data.transactions : transactions;

      if (exportTx.length === 0) return;

      const headers = ["Society", "Date", "Farmer Name", "Father Name", "Mobile No", "Village", "Profile", "Machine", "Hrs", "Charges/Hr", "Machine Rental", "Tractor Required", "Tractor Rental", "Total Rental"];
      const rows = [];

      exportTx.forEach(t => {
        (t.Machines || []).forEach(m => {
          rows.push([
            `"${t.Society || ''}"`,
            `"${formatDate(t.TransactionDate)}"`,
            `"${t.FarmerName || ''}"`,
            `"${t.FatherName || ''}"`,
            `"${t.FarmerMobileNo || ''}"`,
            `"${t.Village || ''}"`,
            `"${t.FarmerProfile || ''}"`,
            `"${m.Machine || ''}"`,
            m.MachineHrs || 0,
            m.ChargesPerHrs || 0,
            m.MachineRental || 0,
            m.TractorRequired ? 'Yes' : 'No',
            m.TractorRental || 0,
            m.TotalRental || 0
          ].join(','));
        });
      });

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    } finally {
      setExporting(false);
    }
  };

  // Filter Bar Slot
  const filterBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        {/* Period Filter Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Period:</label>
          <select
            value={periodInput}
            onChange={(e) => setPeriodInput(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              background: '#ffffff',
              outline: 'none',
              fontWeight: '500'
            }}
          >
            {periods.map(p => (
              <option key={p._id} value={p._id}>
                {p.StartDate} to {p.EndDate} ({p.seasonName})
              </option>
            ))}
          </select>
        </div>

        {/* Society Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Society:</label>
          <select
            value={societyInput}
            onChange={(e) => setSocietyInput(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              background: '#ffffff',
              outline: 'none',
              fontWeight: '500'
            }}
          >
            <option value="ALL">All Assigned Societies ({societies.length})</option>
            {societies.map(s => (
              <option key={s._id} value={s._id}>{s.SOCIETY || s.SocietyName}</option>
            ))}
          </select>
        </div>

        {/* Date Range Inputs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Date:</label>
          <input
            type="date"
            value={startDateInput}
            onChange={(e) => setStartDateInput(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          />
          <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
          <input
            type="date"
            value={endDateInput}
            onChange={(e) => setEndDateInput(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSearchSubmit}
            style={{
              background: '#f59e0b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 18px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClearFilters}
            style={{
              background: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Export CSV Button */}
      <button
        type="button"
        onClick={handleExportCSV}
        disabled={exporting || (totalCount === 0 && transactions.length === 0)}
        style={{
          background: (exporting || (totalCount === 0 && transactions.length === 0)) ? '#cbd5e1' : '#10b981',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          padding: '8px 16px',
          fontWeight: '600',
          fontSize: '13px',
          cursor: (exporting || (totalCount === 0 && transactions.length === 0)) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        {exporting ? '⏳ Exporting...' : '📊 Export CSV'}
      </button>
    </div>
  );

  // Footer Slot with Pagination Controls
  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
      <div style={{ fontSize: '13px', color: '#64748b' }}>
        Showing <b>{totalCount === 0 ? 0 : (page - 1) * limit + 1}</b> to <b>{Math.min(page * limit, totalCount)}</b> of <b>{totalCount}</b> entries
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Rows Per Page Selector */}
        <div className="rows-per-page-container" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
          <span>Rows per page:</span>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              background: '#ffffff',
              outline: 'none'
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Prev / Next Pagination Controls */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setPage(p => Math.max(p - 1, 1))}
            disabled={page === 1 || loading}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: page === 1 ? '#f1f5f9' : '#ffffff',
              color: page === 1 ? '#94a3b8' : '#1e293b',
              fontSize: '13px',
              cursor: page === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>

          <span style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
            Page {page} of {totalPages}
          </span>

          <button
            type="button"
            onClick={() => setPage(p => Math.min(p + 1, totalPages))}
            disabled={page >= totalPages || loading}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: page >= totalPages ? '#f1f5f9' : '#ffffff',
              color: page >= totalPages ? '#94a3b8' : '#1e293b',
              fontSize: '13px',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {toastMessage && (
        <div className="toast-notification">
          <span>✅</span> {toastMessage}
        </div>
      )}
      <ListContainer
        title="Transaction List"
        onClose={() => navigate('/home')}
        filterBar={filterBar}
        footer={footer}
      >
        <div style={{ overflowX: 'auto', maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left', background: '#ffffff' }}>
            <thead>
              <tr style={{ background: '#ffffff', borderBottom: '2px solid #e2e8f0', color: '#334155', fontWeight: '700' }}>
                <th style={{ padding: '14px 16px', width: '120px', verticalAlign: 'top' }}>Society</th>
                <th style={{ padding: '14px 16px', width: '110px', verticalAlign: 'top' }}>Date</th>
                <th style={{ padding: '14px 16px', width: '220px', verticalAlign: 'top' }}>Farmer Details</th>
                <th style={{ padding: '14px 16px', textAlign: 'center', verticalAlign: 'top' }}>Details of Implements rented</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                /* Skeleton Shimmer Loading Rows */
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <td style={{ padding: '16px' }}>
                      <div style={{ height: '16px', background: '#e2e8f0', borderRadius: '4px', width: '90px', animation: 'pulse 1.5s infinite' }}></div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ height: '16px', background: '#e2e8f0', borderRadius: '4px', width: '80px', animation: 'pulse 1.5s infinite' }}></div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '4px', width: '140px', marginBottom: '8px' }}></div>
                      <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '4px', width: '110px', marginBottom: '8px' }}></div>
                      <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '4px', width: '90px' }}></div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ height: '80px', background: '#e2e8f0', borderRadius: '6px', width: '100%' }}></div>
                    </td>
                  </tr>
                ))
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                    No transactions found matching your criteria.
                  </td>
                </tr>
              ) : (
                transactions.map((t) => {
                  const totalMachineHrs = (t.Machines || []).reduce((sum, m) => sum + (m.MachineHrs || 0), 0);
                  const totalChargesPerHrs = (t.Machines || []).reduce((sum, m) => sum + (m.ChargesPerHrs || 0), 0);
                  const totalMachineRental = (t.Machines || []).reduce((sum, m) => sum + (m.MachineRental || 0), 0);
                  const totalTractorRental = (t.Machines || []).reduce((sum, m) => sum + (m.TractorRental || 0), 0);
                  const totalTotalRental = (t.Machines || []).reduce((sum, m) => sum + (m.TotalRental || 0), 0);

                  return (
                    <tr key={t._id} style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      {/* Society */}
                      <td style={{ padding: '16px', verticalAlign: 'top', fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap' }}>
                        {t.Society || 'N/A'}
                      </td>

                      {/* Date */}
                      <td style={{ padding: '16px', verticalAlign: 'top', fontWeight: '500', color: '#475569', whiteSpace: 'nowrap' }}>
                        {formatDate(t.TransactionDate)}
                      </td>

                      {/* Farmer Details */}
                      <td style={{ padding: '16px', verticalAlign: 'top', color: '#334155', lineHeight: '1.6' }}>
                        <div><b>Farmer Name:</b> <span style={{ color: '#64748b' }}>{t.FarmerName}</span></div>
                        <div><b>Village:</b> <span style={{ color: '#64748b' }}>{t.Village}</span></div>
                        <div><b>Mobile No:</b> <span style={{ color: '#64748b' }}>{t.FarmerMobileNo}</span></div>
                        <div><b>Father Name:</b> <span style={{ color: '#64748b' }}>{t.FatherName}</span></div>
                        <div><b>Profile:</b> <span style={{ color: '#64748b' }}>{t.FarmerProfile}</span></div>
                      </td>

                      {/* Details of Implements Rented Sub-table */}
                      <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                        <div style={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#ffffff' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', color: '#1e293b' }}>
                                <th style={{ padding: '8px 10px', minWidth: '130px' }}>ਮਸ਼ੀਨਾਂ<br/>(Machine(s))</th>
                                <th style={{ padding: '8px 10px', width: '90px', textAlign: 'right' }}>ਘੰਟੇ) ਕਿਰਾਇਆ<br/>(Hour(s) Rented)</th>
                                <th style={{ padding: '8px 10px', width: '110px', textAlign: 'right' }}>ਘੰਟੇ ਪ੍ਰਤੀ ਕਿਰਾਇਆ ਕਿਰਾਏ<br/>(Rental Charges Per Hour)</th>
                                <th style={{ padding: '8px 10px', width: '100px', textAlign: 'right' }}>ਕਿਰਾਏ ਦਾ ਖਰਚਾ<br/>(Rental Charges)</th>
                                <th style={{ padding: '8px 10px', width: '100px', textAlign: 'center' }}>ਟਰੈਕਟਰ ਲੋੜੀਂਦਾ<br/>(Tractor Required)</th>
                                <th style={{ padding: '8px 10px', width: '100px', textAlign: 'right' }}>ਟਰੈਕਟਰ ਕਿਰਾਇਆ<br/>(Tractor Rental)</th>
                                <th style={{ padding: '8px 10px', width: '90px', textAlign: 'right' }}>ਕੁਲ ਕਿਰਾਇਆ<br/>(Total Rental)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(t.Machines || []).map((m, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '8px 10px', fontWeight: '500', color: '#475569' }}>{m.Machine}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{m.MachineHrs}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{m.ChargesPerHrs}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{m.MachineRental}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{m.TractorRequired ? 'Yes' : 'No'}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{m.TractorRental}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>{m.TotalRental}</td>
                                </tr>
                              ))}
                              {/* Grand Total Row */}
                              <tr style={{ background: '#f8fafc', fontWeight: '700', color: '#1e293b' }}>
                                <td style={{ padding: '8px 10px' }}>Grand Total</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalMachineHrs}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalChargesPerHrs}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalMachineRental}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>-</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalTractorRental}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalTotalRental}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </ListContainer>
    </>
  );
};

export default InspectorTransactionListPage;
